import React from "react";
import { cva } from "class-variance-authority";
import { IconsMap } from "@/images/icons/icons.map";
import { IconName } from "@/images/icons/types";

export type { IconName };

export type IconColor =
  | "white"
  | "purple"
  | "primary"
  | "grey"
  | "lightGrey"
  | "darkGrey"
  | "black"
  | "blue"
  | "green"
  | "red"
  | "orange";

type IconSize = "2xs" | "xs" | "sm" | "md" | "lg" | "xl";

export interface IconProps extends React.HTMLAttributes<HTMLSpanElement> {
  icon: IconName;
  marginRight?: boolean;
  size?: IconSize;
  color?: IconColor;
}

const iconClass = cva("inline-block", {
  variants: {
    color: {
      white: "text-white",
      purple: "text-purple-500",
      primary: "text-primary",
      grey: "text-gray-500",
      lightGrey: "text-gray-400",
      black: "text-gray-900",
      blue: "text-blue-500",
      green: "text-green-500",
      orange: "text-orange-500",
      red: "text-red-500",
      darkGrey: "text-gray-700",
    },
    size: {
      "2xs": "w-[6px] h-[6px]", // 8px
      xs: "w-3 h-3", // 12px
      sm: "w-4 h-4", // 16px. for some reason w-4 h-4 is 8px not 16px. used rem instead
      md: "w-6 h-6", // 24px (default)
      lg: "w-8 h-8", // 32px
      xl: "w-10 h-10", // 40px
    },
  },
  defaultVariants: {
    color: "darkGrey",
    size: "md",
  },
});

function Icon(props: IconProps) {
  const { icon, marginRight, size, className, color, ...rest } = props;
  const IconComponent = IconsMap[icon];

  if (!IconComponent) {
    return null;
  }

  return (
    <span className={iconClass({ color, size, className })} {...rest}>
      <IconComponent className="w-full h-full" />
    </span>
  );
}

export { Icon };
